import { PresentationContent, ChatMessage } from "../../../types";
import { AiRequestOptions, StreamCallbacks, ToolExecutionPolicy } from "../types";
import type { AIProvider, ModelConfig } from "../../../modelConfig";
import { isOpenAINativeEnabled } from "../openaiNativeRollout";
import { getPowerPointModel } from "../../../../shared/remoteConfig/powerPointModel";
import { findModelPickerEntry } from "../../../../shared/modelPicker/catalog";
import { resolveModelPickerOverride } from "../../../../shared/modelPicker/selectionStore";
import { queryOpenAI } from "./openai";
import { queryAnthropic } from "./anthropic";
import { queryGemini } from "./gemini";

/**
 * The provider for a model id outside the catalog. ConfigCat can serve a new
 * model without a deploy; Claude ids are recognizable by their prefix.
 */
function inferProvider(modelId: string): AIProvider {
  return modelId.startsWith("claude-") ? "anthropic" : "openai";
}

function getPowerPointAgentConfig(): {
  model: ModelConfig;
  options: Pick<AiRequestOptions, "reasoningEffort" | "reasoningMode">;
} {
  const override = resolveModelPickerOverride("powerpoint");
  const configuredModel = getPowerPointModel();
  const entry = override?.entry ?? findModelPickerEntry("powerpoint", configuredModel);
  // ConfigCat may serve either a stable catalog id (`gpt-5.5`) or a direct
  // wire id. Canonicalize known entries so aliases use their pinned provider
  // model, while unknown future ids still pass through without a deploy.
  const model = override?.entry.model ?? entry?.model ?? configuredModel;
  const reasoningMode = entry?.allowedModes?.includes("pro")
    ? (override?.mode ?? entry.defaultMode)
    : undefined;
  return {
    model: {
      id: override?.entry.id ?? entry?.id ?? model,
      name: override?.entry.label ?? entry?.label ?? model,
      provider: entry?.provider ?? inferProvider(model),
      model,
      description: override
        ? "Internal PowerPoint model-picker override"
        : "ConfigCat-selected PowerPoint model",
      maxOutputTokens: entry?.maxOutputTokens,
    },
    options: {
      reasoningEffort: override?.effort ?? "low",
      // Preserve the host default over any lower-level call-site option, and
      // explicitly clear Pro for models whose catalog entry does not allow it.
      reasoningMode,
    },
  };
}

/**
 * Whether this turn will run the native (tool-calling) agent. Claude always
 * does; GPT does unless its native rollout has been switched off, in which
 * case tools that only exist natively (ActiveGraph, PresLink charts) must not
 * be offered.
 */
export function isPowerPointNativeAgentActive(): boolean {
  const { provider } = getPowerPointAgentConfig().model;
  if (provider === "anthropic") return true;
  return provider === "openai" && isOpenAINativeEnabled();
}

export async function queryOpenAIStreaming(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  slideImages?: string | string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const agentConfig = getPowerPointAgentConfig();
  const { model } = agentConfig;
  const runOptions = { ...options, ...agentConfig.options };

  const images = slideImages
    ? Array.isArray(slideImages)
      ? slideImages
      : [slideImages]
    : undefined;
  const hasPreludeUserMessages =
    options.preludeUserMessages?.some((message) => message.trim().length > 0) === true;

  // OpenAI and Claude both run the native agent loop, which carries Skills
  // guidance on every hop; the legacy Gemini path has no prelude support.
  if (model.provider === "google" && hasPreludeUserMessages) {
    callbacks.onError("PowerPoint Skills are not available with the selected AI provider.");
    return;
  }

  switch (model.provider) {
    case "anthropic":
      return queryAnthropic(
        presentationContent,
        userQuery,
        callbacks,
        model,
        images,
        chatHistory,
        toolPolicy,
        runOptions
      );
    case "google":
      return queryGemini(
        presentationContent,
        userQuery,
        callbacks,
        model,
        images,
        chatHistory,
        toolPolicy,
        runOptions
      );
    case "openai":
    default:
      return queryOpenAI(
        presentationContent,
        userQuery,
        callbacks,
        model,
        images,
        chatHistory,
        toolPolicy,
        runOptions
      );
  }
}

export { queryOpenAI } from "./openai";
export { queryAnthropic } from "./anthropic";
export { queryGemini } from "./gemini";
